import { useState } from "react";

export default function CtaSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;
    setLoading(true);
    try {
      const body = new URLSearchParams();
      formData.forEach((val, key) => body.append(key, val.toString()));
      await fetch("https://readdy.ai/api/form/d75hk0ucsbv12mkudmc0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative overflow-hidden"
      style={{ background: "#1a0d28" }}
    >
      {/* Full-bleed background image with overlay */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/14d889452ee06f38e1152c2ce5f5eb91.jpg"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0d28]/90 via-[#4d316c]/50 to-[#1a2a1e]/90" />
      </div>

      {/* Top border accent */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(169,186,171,0.4), transparent)" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div>
            <span className="inline-block text-xs font-semibold tracking-widest uppercase mb-6" style={{ color: "#a9baab" }}>
              Ready to Systemize?
            </span>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-semibold leading-tight tracking-tight text-white mb-6">
              Take Control of
              <br />
              <span style={{ background: "linear-gradient(90deg, #a9baab, #c4aad8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Your Operations.
              </span>
            </h2>
            <p className="text-base md:text-lg leading-relaxed mb-10" style={{ color: "rgba(255,255,255,0.55)" }}>
              Stop losing opportunities due to broken systems. Install a system that works — and start converting every lead with precision and consistency.
            </p>

            {/* Trust points */}
            <div className="flex flex-col gap-5 mb-12">
              {[
                { icon: "ri-time-line", text: "Free 30-minute strategy call included" },
                { icon: "ri-shield-check-line", text: "No contracts, no obligations" },
                { icon: "ri-flashlight-line", text: "System audit delivered within 48 hours" },
              ].map((pt) => (
                <div key={pt.text} className="flex items-center gap-4">
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ background: "rgba(169,186,171,0.12)", border: "1px solid rgba(169,186,171,0.2)" }}
                  >
                    <i className={`${pt.icon} text-sm`} style={{ color: "#a9baab" }}></i>
                  </div>
                  <span className="text-sm" style={{ color: "rgba(255,255,255,0.65)" }}>{pt.text}</span>
                </div>
              ))}
            </div>

            {/* Visual quote */}
            <div
              className="p-6 rounded-2xl"
              style={{ background: "rgba(169,186,171,0.07)", border: "1px solid rgba(169,186,171,0.15)" }}
            >
              <p className="text-sm leading-relaxed italic" style={{ color: "rgba(255,255,255,0.6)" }}>
                &ldquo;Patronum AI took our lead response from hours to seconds. The system practically runs itself.&rdquo;
              </p>
              <div className="flex items-center gap-3 mt-4">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold"
                  style={{ background: "rgba(196,170,216,0.2)", color: "#c4aad8" }}
                >
                  JM
                </div>
                <div>
                  <p className="text-xs font-semibold text-white/70">James M.</p>
                  <p className="text-xs" style={{ color: "rgba(255,255,255,0.35)" }}>Managing Director, Lending Firm</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div
            className="rounded-3xl p-8 md:p-10"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", backdropFilter: "blur(20px)" }}
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-14 text-center gap-5">
                <div
                  className="w-16 h-16 flex items-center justify-center rounded-full"
                  style={{ background: "rgba(169,186,171,0.15)", border: "1px solid rgba(169,186,171,0.3)" }}
                >
                  <i className="ri-check-line text-2xl" style={{ color: "#a9baab" }}></i>
                </div>
                <h3 className="text-xl font-semibold text-white">Request Received</h3>
                <p className="text-sm max-w-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>
                  Thank you — we&apos;ll be in touch within 24 hours to schedule your strategy call.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="strategy-call-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <div className="mb-2">
                  <h3 className="text-xl font-semibold text-white mb-1">Book Your Strategy Call</h3>
                  <p className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>Fill in your details and we&apos;ll reach out within 24 hours.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: "First Name", name: "first_name", type: "text", placeholder: "John" },
                    { label: "Last Name", name: "last_name", type: "text", placeholder: "Smith" },
                  ].map((f) => (
                    <div key={f.name} className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.5)" }}>{f.label}</label>
                      <input
                        type={f.type}
                        name={f.name}
                        required
                        placeholder={f.placeholder}
                        className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                        style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", color: "white" }}
                      />
                    </div>
                  ))}
                </div>

                {[
                  { label: "Email Address", name: "email", type: "email", placeholder: "john@company.com" },
                  { label: "Company", name: "company", type: "text", placeholder: "Company name" },
                ].map((f) => (
                  <div key={f.name} className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.5)" }}>{f.label}</label>
                    <input
                      type={f.type}
                      name={f.name}
                      required={f.name === "email"}
                      placeholder={f.placeholder}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                      style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", color: "white" }}
                    />
                  </div>
                ))}

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.5)" }}>Industry</label>
                  <select
                    name="industry"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none cursor-pointer"
                    style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.75)" }}
                  >
                    <option value="" style={{ background: "#2d1a42" }}>Select your industry</option>
                    <option value="Financial Services" style={{ background: "#2d1a42" }}>Financial Services &amp; Lending</option>
                    <option value="Advisory & Brokerage" style={{ background: "#2d1a42" }}>Advisory &amp; Brokerage</option>
                    <option value="Construction" style={{ background: "#2d1a42" }}>Construction &amp; Project Delivery</option>
                    <option value="Other" style={{ background: "#2d1a42" }}>Other</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium flex justify-between" style={{ color: "rgba(255,255,255,0.5)" }}>
                    <span>Biggest operational challenge?</span>
                    <span style={{ color: "rgba(255,255,255,0.25)" }}>Optional · 500 chars</span>
                  </label>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder="Briefly describe your situation..."
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none"
                    style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", color: "white" }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-xl font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200 hover:opacity-90 disabled:opacity-60 mt-1"
                  style={{ background: "#a9baab", color: "#1a0d28" }}
                >
                  {loading ? "Submitting..." : "Book Your Strategy Call"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
